import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { PriceId } from '@iheartjane/types';
import omit from 'lodash-es/omit';
import { InView } from 'react-intersection-observer';
import { useLocation } from 'react-router';

import type { AlgoliaProduct } from '@jane/search/types';
import {
  buildClickTrackingEvent,
  findSpecialForProduct,
} from '@jane/shared-ecomm/components';
import { useDeepCompareMemo } from '@jane/shared-ecomm/hooks';
import { useUserPreferences } from '@jane/shared-ecomm/providers';
import { track, trackProductListingTap } from '@jane/shared-ecomm/tracking';
import { LegacyMenuProductCard } from '@jane/shared/components';
import type { LegacyMenuProductCardProps } from '@jane/shared/components';
import { useUserSegmentIds } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type {
  StoreSpecial,
  PendingCartProduct as tPendingCartProduct,
} from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import type { PendingCartProduct } from '@jane/shared/types';
import { deserializeCannabinoids } from '@jane/shared/util';

import styles from './MenuQuickCheckoutDrawer.module.css';

export type HitProductCardProps = Omit<
  LegacyMenuProductCardProps,
  | 'appMode'
  | 'brand'
  | 'cartIsOpen'
  | 'cartProducts'
  | 'cartStoreId'
  | 'carouselView'
  | 'currentSpecial'
  | 'itemWidth'
  | 'listView'
  | 'onClick'
  | 'onAddToCart'
  | 'onDeleteFromCart'
  | 'product'
  | 'userLocation'
> & {
  algoliaIndexName: string;
  bucketName?: string;
  cartProducts: PendingCartProduct[];
  cartStoreId?: number | string;
  columnPosition?: number;
  hit: AlgoliaProduct;
  onAddToCart: (productId: number, priceId: PriceId, count: number) => void;
  productLocation?: string;
  rowPosition?: number;
  specials?: StoreSpecial[];
};

export const HitProductCard = ({
  algoliaIndexName,
  bucketName,
  cartProducts,
  cartStoreId,
  columnPosition,
  hit,
  onAddToCart,
  productLocation = '',
  rowPosition,
  specials,
  store,
  productInstance,
  ...props
}: HitProductCardProps) => {
  const { userLocation, userPreferences = {} } = useUserPreferences();
  const { storeFulfillmentType, storeSearchRadius, storeType } =
    userPreferences;
  const { pathname } = useLocation();
  const { appMode } = useEcommApp();
  const c1BundleTag = useFlag(FLAGS.c1BundleTag);

  const {
    city: storeCity,
    state: storeState,
    name: storeName,
    id: storeId,
  } = store;

  const menuProduct = useDeepCompareMemo<any>(() => {
    const mp = {
      ...deserializeCannabinoids(hit, 'menuProduct'),
      id: hit['product_id'],
    };
    return omit(mp, 'product_id');
  }, [hit]);

  const userSegments = useUserSegmentIds();

  const clickTrackingEvent = buildClickTrackingEvent({
    indexName: algoliaIndexName,
    store,
    menuProduct,
    carouselView: true,
    rowPosition,
    columnPosition,
    listView: false,
    menuRowName: bucketName,
    userSegments,
  });

  return (
    <InView
      className={styles['inViewFlex']}
      data-testid="hit-product-card"
      delay={1000}
      onChange={(isInView) => {
        if (isInView) {
          productInstance?.impress();
        }
      }}
      threshold={0.5}
      triggerOnce
    >
      <LegacyMenuProductCard
        appMode={appMode}
        // This card renders inside the quick-checkout drawer, not the cart
        // drawer; a click here must not close the cart drawer behind it.
        cartIsOpen={false}
        cartProducts={cartProducts as tPendingCartProduct[]}
        cartStoreId={cartStoreId}
        currentSpecial={findSpecialForProduct(
          menuProduct,
          specials,
          c1BundleTag
        )}
        listView={false}
        product={menuProduct}
        productInstance={productInstance}
        onAddToCart={(addData) => {
          onAddToCart(addData.menuProduct.id, addData.price_id, addData.count);
          productInstance?.click({ isAddToCart: true });
        }}
        onClick={() => {
          productInstance?.click();
          clickTrackingEvent && track(clickTrackingEvent);
          trackProductListingTap({
            cityState: userLocation?.cityState,
            product: menuProduct,
            productLocation,
            source: pathname,
            storeId: String(storeId),
            storeName,
            storeCity: storeCity ?? undefined,
            storeState: storeState ?? undefined,
            storeFulfillmentType,
            storeSearchRadius,
            storeType,
            userSegments,
            zipcode: userLocation?.zipcode,
          });
        }}
        {...props}
        store={store}
        userLocation={userLocation}
      />
    </InView>
  );
};
